import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { Button, Drawer, DrawerFooter, DrawerHeader } from '@/design-system';
import { orderApi, orderKeys, type Order } from '@/entities/order';
import { reportKeys } from '@/entities/report';
import { errorMessage } from '@/shared/api';

import { ObjectAddressFields } from '@/features/inspections/components/ObjectAddressFields';
import { useObjectAddress } from '@/features/inspections/objectAddress';

import { dossierCopy } from '../copy';

export interface ChangeAddressPanelProps {
    order: Order;
    open: boolean;
    onClose: () => void;
    /** Na opslaan: het dossier zegt op welk adres de keuring nu staat. */
    onSaved: (order: Order) => void;
}

/**
 * "Adres van het object wijzigen": de keuring op het juiste adres zetten, met
 * dezelfde BAG-zoektocht als bij een nieuwe keuring ({@link useObjectAddress}).
 * Het object wordt gevonden of gemaakt, en daarna verhuist alleen deze keuring
 * erheen (`POST /orders/{order}/address`); andere keuringen op het oude adres
 * blijven staan. Een rapport dat nog niet verstuurd is, neemt het nieuwe adres
 * over; daarom lezen rapport, dossier en werklijst na opslaan opnieuw.
 */
export function ChangeAddressPanel({ order, open, onClose, onSaved }: ChangeAddressPanelProps) {
    const copy = dossierCopy.changeAddress;

    return (
        <Drawer open={open} onClose={onClose} side="right" width={480} title={copy.title}>
            <DrawerHeader
                icon="edit"
                title={copy.title}
                description={copy.description}
                onClose={onClose}
                closeLabel={copy.close}
            />
            {/* Een dichte Drawer rendert niets: elk openen begint met lege velden. */}
            <ChangeAddressForm order={order} onCancel={onClose} onSaved={onSaved} />
        </Drawer>
    );
}

function ChangeAddressForm({
    order,
    onCancel,
    onSaved,
}: {
    order: Order;
    onCancel: () => void;
    onSaved: (order: Order) => void;
}) {
    const copy = dossierCopy.changeAddress;
    const queryClient = useQueryClient();
    const address = useObjectAddress();
    const [incomplete, setIncomplete] = useState(false);
    const [preparing, setPreparing] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const save = useMutation({
        mutationFn: (propertyUuid: string) => orderApi.changeAddress(order.uuid, propertyUuid),
        onSuccess: (moved) => {
            void queryClient.invalidateQueries({ queryKey: orderKeys.all });
            void queryClient.invalidateQueries({ queryKey: reportKeys.all });
            onSaved(moved);
        },
        onError: (error) => setProblem(errorMessage(error)),
    });

    const busy = preparing || save.isPending;

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setProblem(null);
        setIncomplete(!address.ready);

        if (!address.ready) {
            return;
        }

        setPreparing(true);

        try {
            const propertyUuid = await address.ensureProperty();

            setPreparing(false);
            save.mutate(propertyUuid);
        } catch (error) {
            setPreparing(false);
            setProblem(errorMessage(error));
        }
    }

    return (
        <form
            onSubmit={(event) => void submit(event)}
            className="flex min-h-0 flex-1 flex-col pt-5"
            noValidate
        >
            <div className="mx-5 mb-6 grid gap-3">
                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                    {copy.current(order.property.fullAddress)}
                </p>
                <ObjectAddressFields address={address} />
                {incomplete && !address.ready ? (
                    <p role="alert" className="m-0 text-[12px] leading-[1.4] text-[var(--danger)]">
                        {copy.incomplete}
                    </p>
                ) : null}
            </div>

            {problem ? (
                <p
                    role="alert"
                    className="m-0 mx-5 mb-4 text-[13px] leading-[1.5] text-[var(--danger)]"
                >
                    {problem}
                </p>
            ) : null}

            <DrawerFooter>
                <Button type="submit" variant="primary" size="md" disabled={busy}>
                    {busy ? copy.saving : copy.save}
                </Button>
                <Button variant="outline" size="md" disabled={busy} onClick={onCancel}>
                    {copy.cancel}
                </Button>
            </DrawerFooter>
        </form>
    );
}
